

import { Component, Emit, Prop, Vue } from "vue-property-decorator";
import vSelect3 from "vselect3";
import NotificationRuleAction from "@/types/sv-data/notification-rule/NotificationRuleAction";
import NotificationTemplateFormatOption, { notificationTemplateFormats } from "@/types/sv-data/notification-templates/NotificationTemplateFormatOption";
import ContactGroupSelect from "../contact-group/ContactGroupSelect.vue";
import ContactGroup from "@/types/sv-data/contact-groups/ContactGroup";
import NotificationRuleActionContactMembership from "@/types/sv-data/notification-rule/NotificationRuleActionContactMembership";
import NotificationTemplatesSelect from "./NotificationTemplatesSelect.vue";
import { cloneDeep } from "lodash";
import NotificationTemplate from "@/types/sv-data/notification-templates/NotificationTemplate";
import ShortContact from "@/types/sv-data/users/ShortContact";
import ContactEmailSelect from "@/components/contacts/ContactEmailSelect.vue";
import { Getter } from "vuex-class";

@Component({
    components: {
        "v-select-3": vSelect3,
        "contact-group-select": ContactGroupSelect,
        "notification-template-select": NotificationTemplatesSelect,
        "contact-email-select": ContactEmailSelect
    },
})
export default class NotificationRuleActionDetails extends Vue {

    @Prop({ type: Boolean, default: false })
    private readOnly: boolean;

    @Prop({type: Object, required: true})
    private action: NotificationRuleAction;

    @Prop({type: Boolean, required: true})
    private allowDelete: boolean;

    @Prop({ type: Array, required: false })
    private notificationTemplates: NotificationTemplate[];

    @Prop({type: Array, required: true})
    private contacts: ShortContact[];
    
	@Getter("getFeature") getFeature: (featureName: string[]) => boolean;

    private notificationTemplateFormatOptions: NotificationTemplateFormatOption[] = notificationTemplateFormats;
    private originalAction: NotificationRuleAction | null = null;

    private mounted(): void {
        if (!this.originalAction) {
            this.originalAction = cloneDeep(this.action);
        }
        this.checkValidation();
    }

	private get isContactGroupsEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "Email", "NotificationTemplates", "NotificationGroups"]);
	}

    private get selectedContactGroupIds(): string[] {
        return this.action.sendTo.map((cm: NotificationRuleActionContactMembership) => cm.contactGroupId);
    }

    private get selectedContacts(): ShortContact[] {
        const contactMemberships = this.action.sendTo.filter((cm: NotificationRuleActionContactMembership) => !cm.contactGroupId);
        const users = this.contacts.filter((contact: ShortContact) => !!contact.userID);
        var res = contactMemberships.map((contact: NotificationRuleActionContactMembership) => {
            return {
                userID: contact.userId,
                email: contact.emailAddress,
                fullName: contact.userId ? (users.firstOrDefault((u: ShortContact) => u.userID === contact.userId))?.fullName : contact.emailAddress
            };
        });
        return res;
    }

    private get areContactsRequired(): boolean {
        return this.action.sendTo?.length < 1;
    }
    
    private updateAction(): void {
        this.$emit("updateAction", this.action);
        this.checkValidation();
    }
    
    @Emit("deleteAction")
    private onDelete(): void {
        return;
    }
    
    @Emit("updateValid")
    private checkValidation(): boolean {
        return this.isValidAction();
    }

    private isValidAction(): boolean {
        return !!this.action.title && 
            !!this.action.sendTo && 
            this.action.sendTo.length !== 0;
    }

    private onContactGroupsUpdated(contactGroups: ContactGroup[]): void {
        const newContactGroupIds: string[] = contactGroups.map(cg => cg.contactGroupId);
        const membershipsToKeep: NotificationRuleActionContactMembership[] = 
            this.action.sendTo.filter((cm: NotificationRuleActionContactMembership) => newContactGroupIds.includes(cm.contactGroupId) || !!cm.userId || !!cm.emailAddress);

        const existingMembershipsIds = membershipsToKeep.map((cm: NotificationRuleActionContactMembership) => cm.contactGroupId);
        const contactGroupsToAdd = contactGroups.filter((cg: ContactGroup) => !existingMembershipsIds.includes(cg.contactGroupId))

        const membershipsToAdd: NotificationRuleActionContactMembership[] = contactGroupsToAdd.map((cg: ContactGroup) => {
            return {
                contactGroupId: cg.contactGroupId,
                userId: null,
                emailAddress: null,
                notificationRuleActionId: isNaN(this.action.notificationRuleActionId) ? null : this.action.notificationRuleActionId,
                tenantId: null,
            }
        })
        this.action.sendTo = [...membershipsToKeep, ...membershipsToAdd];
        this.updateAction();
    }

    private onEmailsUpdated(emails:  ShortContact[]): void {
        const contacts: number[] = emails.filter(em => !!em.userID).map(em => em.userID);
        const emailOnly: string[] = emails.filter(em => !em.userID && this.isValidEmail(em.email)).map(em => em.email);
        const membershipsToKeep = 
            this.action.sendTo.filter((cm: NotificationRuleActionContactMembership) => contacts.includes(cm.userId) || emailOnly.includes(cm.emailAddress) || !!cm.contactGroupId);

        const contactsToKeepIds = 
            membershipsToKeep.filter((cm: NotificationRuleActionContactMembership) => !!cm.userId).map((cm: NotificationRuleActionContactMembership) => cm.userId);

        const emailsToKeep = membershipsToKeep.filter((cm: NotificationRuleActionContactMembership) => !cm.userId && !!cm.emailAddress).map((cm: NotificationRuleActionContactMembership) => cm.emailAddress); 
        const contactsToAdd = emails.filter(em => !contactsToKeepIds.includes(em.userID) && !emailsToKeep.includes(em.email));

        const toAdd: ShortContact[] = [...contactsToAdd];

        const membershipsToAdd: NotificationRuleActionContactMembership[] = toAdd.map((em: ShortContact) => {
            return {
                contactGroupId: null,
                userId: em.userID ? em.userID : null,
                emailAddress: !em.userID ? em.email : null,
                notificationRuleActionId: isNaN(this.action.notificationRuleActionId) ? null : this.action.notificationRuleActionId,
                tenantId: null,
            }
        });

        this.action.sendTo = [ ...membershipsToKeep, ...membershipsToAdd ];
        this.updateAction();
    }

    private isValidEmail(email: string): boolean {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(email);
    }
}
